import { useEffect, useRef } from "react";

import { useAuth } from "../../auth/AuthContext";
import { alLlegarMensaje, traerLibreta } from "../../services/mensajes";
import {
  CADA_MS,
  esParaAvisar,
  hayQueParpadear,
  tituloAviso,
} from "../../shared/pestanaParpadea";

/* La pestaña parpadea cuando llega un mensaje (Igna, 23/10/2026).
 *
 * Lo monta `LayoutSwitcher`, así que escucha desde cualquier pantalla del hub
 * y con los dos armazones: el mensaje puede llegar mientras uno está en
 * Tractores, y la pantalla de Mensajes ni está montada.
 *
 * Mientras la pestaña está de fondo (u otra ventana tiene el foco) el título
 * alterna con «(N) Mensaje de …» y el ícono lleva un punto rojo. Al volver,
 * todo queda como estaba. No dibuja nada en la página. */

const avisoDeIcono = (() => {
  let cache = null;
  return (href) =>
    new Promise((resolver) => {
      if (cache) return resolver(cache);
      try {
        const img = new Image();
        img.onload = () => {
          try {
            const lado = 64;
            const lienzo = document.createElement("canvas");
            lienzo.width = lado;
            lienzo.height = lado;
            const c = lienzo.getContext("2d");
            c.drawImage(img, 0, 0, lado, lado);
            c.beginPath();
            c.arc(lado * 0.74, lado * 0.26, lado * 0.24, 0, 2 * Math.PI);
            c.fillStyle = "#e0102b";
            c.fill();
            cache = lienzo.toDataURL("image/png");
            resolver(cache);
          } catch {
            resolver(null);
          }
        };
        img.onerror = () => resolver(null);
        img.src = href;
      } catch {
        resolver(null);
      }
    });
})();

const linkDelIcono = () => document.querySelector("link[rel~='icon']");

const PestanaParpadea = () => {
  const { user } = useAuth();
  const yo = (user?.email || "").toLowerCase();

  const estado = useRef({
    cuantos: 0,
    titulo: null, // el de antes de empezar a parpadear
    icono: null,
    aviso: "",
    reloj: null,
    nombres: null,
  });

  useEffect(() => {
    if (!yo) return undefined;
    const e = estado.current;

    const oculta = () => document.hidden;
    const conFoco = () =>
      typeof document.hasFocus === "function" ? document.hasFocus() : true;

    const parar = () => {
      if (e.reloj) clearInterval(e.reloj);
      e.reloj = null;
      if (e.titulo !== null) document.title = e.titulo;
      const link = linkDelIcono();
      if (link && e.icono) link.href = e.icono;
      e.titulo = null;
      e.icono = null;
      e.cuantos = 0;
    };

    const empezar = () => {
      if (e.reloj) return;
      e.titulo = document.title;
      const link = linkDelIcono();
      if (link) {
        e.icono = link.getAttribute("href");
        avisoDeIcono(link.href).then((punto) => {
          if (punto && e.reloj && linkDelIcono()) linkDelIcono().href = punto;
        });
      }
      let mostrarAviso = true;
      document.title = e.aviso;
      e.reloj = setInterval(() => {
        mostrarAviso = !mostrarAviso;
        document.title = mostrarAviso ? e.aviso : e.titulo;
      }, CADA_MS);
    };

    const nombreDe = async (mail) => {
      try {
        if (!e.nombres) {
          const libreta = await traerLibreta();
          e.nombres = {};
          (libreta || []).forEach((p) => {
            if (p.mail) e.nombres[String(p.mail).toLowerCase()] = p.nombre;
          });
        }
        return e.nombres[String(mail || "").toLowerCase()] || "";
      } catch {
        return "";
      }
    };

    const alVolver = () => {
      if (!hayQueParpadear({ oculta: oculta(), conFoco: conFoco() })) parar();
    };

    const dejar = alLlegarMensaje(async (m) => {
      if (!esParaAvisar(m, yo)) return;
      if (!hayQueParpadear({ oculta: oculta(), conFoco: conFoco() })) return;
      const nombre = await nombreDe(m.de);
      // Pudo volver a la pestaña mientras se buscaba el nombre.
      if (!hayQueParpadear({ oculta: oculta(), conFoco: conFoco() })) return;
      e.cuantos += 1;
      e.aviso = tituloAviso(e.cuantos, nombre, m.de);
      if (e.reloj) document.title = e.aviso;
      else empezar();
    });

    document.addEventListener("visibilitychange", alVolver);
    window.addEventListener("focus", alVolver);
    return () => {
      dejar();
      document.removeEventListener("visibilitychange", alVolver);
      window.removeEventListener("focus", alVolver);
      parar();
    };
  }, [yo]);

  return null;
};

export default PestanaParpadea;
